import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
import {Layout, Paragraph, Strong} from "../../../components/blog/layout";
import {DotDotDot, Image, HeaderWithAnchor, Note, Callout, FootnoteLink, Footnote, YouTube, EmbedLink, EmbedGithubGist} from "../../../components/blog/helpers";
const MDXLayout = Layout({
  postId: "cpp-if-statement-variables"
});
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    em: "em",
    p: "p",
    strong: "strong",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: "Like all good blog posts, this one first came to me while I was banging my head against the keyboard, cursing and screaming out for a maternal figure to hold me while I incoherently babbled about a crash caused by seemingly-nonsensical code."
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "“This shouldn’t compile! This CAN’T compile!”"
      })
    }), "\n", _jsx(_components.p, {
      children: "And yet, despite the odds, C++ had decided yet again to show me a new side of it. Please take a moment to peruse the following example code, based on the original that caused me such pain, and see if you can spot the bug that causes a nullptr exception."
    }), "\n", _jsx(EmbedGithubGist, {
      id: "d5c7223331c2786b7c42698209eb1492"
    }), "\n", _jsx(_components.p, {
      children: "Of course, to compile this properly we need some further definitions. Check out https://repl.it/@Winwardo/IfElse-Chain-With-Bug for the full example."
    }), "\n", _jsxs(_components.p, {
      children: ["To save you the trouble, the error is on line 12, where I attempt to access ", _jsx(_components.code, {
        children: "asDerivedA->predicate"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["That’s right: despite declaring ", _jsx(_components.code, {
        children: "asDerivedA"
      }), " in a separate if statement above, I’m able to access it in an else/if statements below. No, this is not a compiler bug; this is clearly and unambiguously defined in the C++ specification. Before we get to that, let’s take a look at some code."]
    }), "\n", _jsxs(_components.p, {
      children: ["If you’re new to the syntax that’s used in the code sample, ", _jsx(_components.code, {
        children: "if (int i = 5) {"
      }), " is a perfectly valid way of declaring and defining a variable, then using it inside the given if statement. It allows us to write terser, clearer code, while also avoiding limiting the scope of a variable. It’s good practise to keep the scope of a variable as small as possible, both to avoid accidental re-use, and to lower the mental overhead on future programmers."]
    }), "\n", _jsx(HeaderWithAnchor, {
      id: "whats-scope",
      number: 1,
      children: _jsx(_components.p, {
        children: "Wait, what's scope?"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["If you’ve not come across the term before, the ", _jsx(_components.strong, {
        children: "scope"
      }), " of a variable is the code it’s accessible in. Variables defined within a function, if statement, or other control block are ", _jsx(_components.strong, {
        children: "scoped"
      }), " to that block. This is important to stop us accidentally re-using variables that were declared elsewhere in code."]
    }), "\n", _jsx(_components.p, {
      children: "The concept of a scope is used in most languages — all major languages, including C++, Javascript, C#, Rust, even Haskell, have a concept of scope. Note that specific definitions and rules vary from language to language."
    }), "\n", _jsxs(_components.p, {
      children: ["You might have also heard of ", _jsx(_components.strong, {
        children: "global scope"
      }), " — this is where a variable is accessible from anywhere, inside or outside a function or block. This is ", _jsx(_components.a, {
        href: "http://wiki.c2.com/?GlobalVariablesAreBad",
        children: "considered poor practise"
      }), " in many cases."]
    }), "\n", _jsx(HeaderWithAnchor, {
      id: "declaring-in-an-if",
      number: 2,
      children: _jsx(_components.p, {
        children: "What’s happening when we declare inside an if statement? 🐿️"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Scope in C++ isn’t just for functions — any time you open a new pair of curly braces, or ", _jsx(_components.strong, {
        children: "block"
      }), ", you create a new scope. You should also know that if we’re declaring (and defining) inside the if statement, the condition then becomes the result of the definition, converted to a boolean. For converting a number to boolean, we ask the question “Is this number not 0?” If the number is 0, the boolean is false, otherwise it’s true."]
    }), "\n", _jsx(_components.p, {
      children: "Knowing this, we can transform a simple if declaration as such:"
    }), "\n", _jsx(EmbedGithubGist, {
      id: "da91e4539ff87573c68336afdfc4209c"
    }), "\n", _jsxs(_components.p, {
      children: ["Both ", _jsx(_components.code, {
        children: "first"
      }), " and ", _jsx(_components.code, {
        children: "second"
      }), " are semantically the same, and produce identical assembler output from the compiler. You can prove this to yourself here: https://godbolt.org/g/zRC8BT"]
    }), "\n", _jsxs(_components.p, {
      children: ["If you think this is a fluke or want further proof that this is the case, here you can see the C++ specification Decreeing That It Must Be So, in section ", _jsx(_components.strong, {
        children: "[stmt.select]"
      }), ". Don’t worry if this section of this blog-post is confusing!"]
    }), "\n", _jsx(Image, {
      src: require("./2_cpp_spec.png"),
      srcSmall: require("./2_cpp_spec.png"),
      alt: "Part of the C++ specification. Follow the link for text.",
      caption: _jsxs(_Fragment, {
        children: ["The C++ specification shows that any variable declared inside the if condition, is hoisted to outside and available inside both the if and else statements. You can view the current C++ specification draft at", " ", _jsx(_components.a, {
          href: "http://eel.is/c++draft/stmt.select",
          children: "http://eel.is/c++draft/stmt.select"
        })]
      }),
      width: "456",
      height: "259"
    }), "\n", _jsxs(_components.p, {
      children: ["Because of this choice, we can see that any variables declared inside the ", _jsx(_components.strong, {
        children: "init-statement"
      }), " ", _jsx(_components.em, {
        children: "must"
      }), " be accessible to the else statement. This alone may not seem enough to cause the bug above, however, due to C++’s resolution to the ", _jsx(_components.a, {
        href: "https://en.wikipedia.org/wiki/Dangling_else",
        children: "dangling else problem"
      }), ", it means variables declared in the ", _jsx(_components.strong, {
        children: "init-statement"
      }), " must also be visible in any else-if statements."]
    }), "\n", _jsx(_components.p, {
      children: "If this is not clear, note that the following two functions are identical:"
    }), "\n", _jsx(EmbedGithubGist, {
      id: "478fba59788cfcecb6ebdc4bdf40c061"
    }), "\n", _jsx(HeaderWithAnchor, {
      id: "this-is-a-neat-trick",
      number: 3,
      children: _jsx(_components.p, {
        children: "This is a really neat trick! I can already see where I can use this! 😍"
      })
    }), "\n", _jsx("span", {
      className: "text-4xl font-bold text-center mt-12",
      children: "NO."
    }), "\n", _jsx("span", {
      className: "text-4xl text-center mt-2",
      children: "STOP RIGHT THERE."
    }), "\n", _jsx("span", {
      className: "text-3xl text-center mt-12",
      children: "Don’t."
    }), "\n", _jsx("span", {
      className: "text-4xl font-bold text-center mt-2",
      children: "YOU."
    }), "\n", _jsx("span", {
      className: "text-5xl font-bold text-center mt-2 mb-8",
      children: "DARE."
    }), "\n", _jsx(_components.p, {
      children: "Having discussed with several colleagues, we’ve been unable to come up with a single legitimate use case for this behaviour. Every example we’ve considered can be rewritten in a much clearer and less surprising fashion."
    }), "\n", _jsx(_components.p, {
      children: "To entertain the idea however, here is the sort of code you may have the misfortune of stumbling across."
    }), "\n", _jsxs(_components.p, {
      children: ["Note that we’ve encoded some state that can be accessed via ", _jsx(_components.code, {
        children: "operator bool()"
      }), ", which will be called when we convert the data to a boolean type."]
    }), "\n", _jsxs(_components.p, {
      children: ["This is not immediately clear code, and it’s ", _jsx(_components.a, {
        href: "https://topher.io/writing/principle-least-astonishment-javascript-sort",
        children: "certainly astonishing"
      }), ". Intent has been obscured behind layers of abstraction that don’t provide us any real benefit."]
    }), "\n", _jsx(EmbedGithubGist, {
      id: "4d40839fb6b3435a05808946f68ec3a0"
    }), "\n", _jsx(Image, {
      src: require("./1_dog.jpg"),
      srcSmall: require("./1_dog.jpg"),
      alt: "A sad looking dog wrapped up in a blanket.",
      caption: _jsxs(_Fragment, {
        children: ["This is how your colleagues will feel if they come across you using this anti-pattern.", " ", _jsx(_components.a, {
          href: "https://unsplash.com/photos/2Ts5HnA67k8",
          children: "Matthew Henry"
        })]
      }),
      width: "1000",
      height: "657"
    }), "\n", _jsx(HeaderWithAnchor, {
      id: "why-was-this-in-your-code",
      number: 4,
      children: _jsx(_components.p, {
        children: "If you’re suggesting against this, why was it in your code base? 🤔"
      })
    }), "\n", _jsx(_components.p, {
      children: "That’s a fair question. I was working on a data-exporter, which had to legitimately cast a pointer to one of several potential types in order to extract information for serialization. (No, polymorphism would not have been the solution here.)"
    }), "\n", _jsxs(_components.p, {
      children: ["It was an if-else chain, and I had copy-pasted one part, forgetting to update the variable name in the process. As shown by PVS Studio’s brilliant article titled ", _jsx(_components.a, {
        href: "https://www.viva64.com/en/b/0260/",
        children: "The Last Line Effect"
      }), ", copying and pasting similar lines downwards regularly results in a bug."]
    }), "\n", _jsx(HeaderWithAnchor, {
      id: "conclusion",
      number: 5,
      children: _jsx(_components.p, {
        children: "To conclude"
      })
    }), "\n", _jsx(_components.p, {
      children: "C++ is a big language, and despite having been working in it for 5 years, it still reveals new parts of itself to me every day. It’s a nice reminder that, even if you think you’re writing code well, without static analysis tools and effective automated testing, it’s very easy to miswrite some code and introduce a crash to a system."
    }), "\n", _jsxs(_components.p, {
      children: ["Have you come across this curiosity anywhere? Can you think of an example where it would be useful to use? Let us know on ", _jsx(_components.a, {
        href: "https://twitter.com/Winwardo",
        children: "Twitter"
      }), " 😄"]
    }), "\n", _jsxs(_components.p, {
      children: ["Thanks to ", _jsx(_components.a, {
        href: "https://twitter.com/JessBoneBreaker",
        children: "Jessica Baker"
      }), ", ", _jsx(_components.a, {
        href: "https://twitter.com/darthtelle",
        children: "Chantelle Porritt"
      }), ", ", _jsx(_components.a, {
        href: "https://twitter.com/andybastable",
        children: "Andy Bastable"
      }), " and ", _jsx(_components.a, {
        href: "https://twitter.com/stukmilne",
        children: "Stuart Milne"
      }), " for proof-reading and discussions."]
    })]
  });
}
export default function MDXContent(props = {}) {
  return _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  });
}
